import React, { useState, useContext } from "react";
import {
  Button,
  message,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Divider,
  Space,
  Card,
} from "antd";
import {
  FileTextOutlined,
  PlusOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { AppContext } from "../context/AppContext";

const { Option } = Select;

const JutemStatementGenerator = ({
  loan,
  borrower,
  transactions = [],
  amortizationSchedule = [],
  buttonProps = {},
}) => {
  const { company } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();

  const openModal = () => {
    const borrowerName =
      `${borrower?.first_name || ""} ${borrower?.last_name || ""}`.trim() ||
      borrower?.borrower_name ||
      "";
    const borrowerNrc =
      borrower?.nrc ||
      borrower?.national_id ||
      borrower?.nrc_number ||
      borrower?.id_number ||
      "";
    const borrowerPhone =
      borrower?.phone ||
      borrower?.phone_number ||
      borrower?.mobile ||
      borrower?.contact ||
      "";

    // Build loan entries from amortization schedule or from loan data
    const loanEntries = [];
    if (amortizationSchedule.length > 0) {
      amortizationSchedule.forEach((inst) => {
        loanEntries.push({
          principal_amount: parseFloat(inst.amount_due || loan?.amount || 0),
          interest_rate: parseFloat(inst.interest_rate || 0),
          interest_period: "Per Period",
          expected_amount: parseFloat(inst.expected_amount || 0),
          due_date: inst.due_date ? moment(inst.due_date) : null,
        });
      });
    }

    if (loanEntries.length === 0) {
      loanEntries.push({
        principal_amount: parseFloat(loan?.amount || loan?.loan_amount || 0),
        interest_rate: parseFloat(
          loan?.interest_applied ||
            loan?.custom_interest_rate ||
            loan?.loan_product?.interest_rate ||
            0
        ),
        interest_period: "Per Period",
        expected_amount: parseFloat(loan?.expected_amount || loan?.amount || 0),
        due_date: loan?.due_date ? moment(loan.due_date) : null,
      });
    }

    // Build payment entries from transactions
    const paymentEntries = [];
    if (transactions.length > 0) {
      transactions.forEach((tx) => {
        if (
          tx.transaction_status === "Completed" ||
          tx.transaction_status === "completed"
        ) {
          paymentEntries.push({
            amount: parseFloat(tx.amount || 0),
            date: tx.transaction_date ? moment(tx.transaction_date) : null,
          });
        }
      });
    }

    form.setFieldsValue({
      client_name: borrowerName,
      client_id: borrowerNrc,
      contact_number: borrowerPhone,
      statement_date: moment(),
      loans: loanEntries,
      payments: paymentEntries,
      late_fee_amount: 0,
      days_past_due: 0,
      interest_expense_amount: 0,
      interest_due_date: null,
    });

    setModalVisible(true);
  };

  const generateStatementPDF = (values) => {
    setLoading(true);
    try {
      const formatAmount = (amount) => {
        const num = parseFloat(amount || 0);
        return `ZMW ${num.toLocaleString("en-ZM", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      };

      // Calculate totals
      const loans = values.loans || [];
      const payments = values.payments || [];

      const totalExpected = loans.reduce(
        (sum, l) => sum + parseFloat(l.expected_amount || 0),
        0
      );
      const totalPaid = payments.reduce(
        (sum, p) => sum + parseFloat(p.amount || 0),
        0
      );
      const lateFee = parseFloat(values.late_fee_amount || 0);
      const interestExpense = parseFloat(values.interest_expense_amount || 0);
      const balanceDue = totalExpected + lateFee + interestExpense;
      const balanceAfterPayments = balanceDue - totalPaid;

      // Build loan terms rows
      const loanTermsRows = loans
        .map((l, idx) => {
          const loanLabel = loans.length > 1 ? ` (Loan ${idx + 1})` : "";
          return `
          <tr><td>Principal Amount${loanLabel}</td><td>${formatAmount(l.principal_amount)}</td></tr>
          <tr><td>Interest Rate (${l.interest_period || "per period"})${loanLabel}</td><td>${parseFloat(l.interest_rate || 0)}%</td></tr>
          <tr><td>Expected Amount${loanLabel}</td><td>${formatAmount(l.expected_amount)}</td></tr>
          <tr><td>Due Date${loanLabel}</td><td>${l.due_date ? moment(l.due_date).format("Do MMMM YYYY") : "N/A"}</td></tr>
        `;
        })
        .join("");

      // Build payment rows
      const paymentRows = payments
        .map(
          (p) => `
        <tr>
          <td>${p.date ? moment(p.date).format("Do MMMM YYYY") : "N/A"}</td>
          <td>${formatAmount(p.amount)}</td>
        </tr>
      `
        )
        .join("");

      // Company logo URL
      const logoUrl = company?.logo || "";

      const html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Jutem Fund - Loan Statement</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: Arial, sans-serif;
      color: #333;
      padding: 30px;
      max-width: 800px;
      margin: 0 auto;
    }
    .header {
      text-align: center;
      margin-bottom: 20px;
      padding-bottom: 15px;
    }
    .logo {
      max-width: 160px;
      max-height: 100px;
      margin-bottom: 8px;
    }
    .company-name {
      font-size: 22px;
      font-weight: bold;
      color: #1a3a5c;
    }
    .company-tagline {
      font-size: 10px;
      color: #666;
      margin-bottom: 5px;
    }
    .statement-title {
      font-size: 18px;
      font-weight: bold;
      text-transform: uppercase;
      color: #000000;
      margin: 15px 0 5px;
      border-bottom: 2px solid #a8d4f0;
      padding-bottom: 5px;
    }
    .section-title {
      font-size: 14px;
      font-weight: bold;
      color: #000000;
      margin: 20px 0 8px;
      border-bottom: 1px solid #ccc;
      padding-bottom: 3px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 15px;
      font-size: 12px;
    }
    table th, table td {
      border: 1px solid #ccc;
      padding: 6px 10px;
      text-align: left;
    }
    table th {
      background-color: #a8d4f0;
      color: #1a3a5c;
      font-weight: bold;
    }
    .balance-table td:first-child {
      font-weight: bold;
      background-color: #f5f5f5;
    }
    .balance-table td:last-child {
      font-weight: bold;
    }
    .total-row td {
      font-weight: bold;
      background-color: #f0f0f0;
    }
    .footer {
      margin-top: 30px;
      font-size: 10px;
      color: #777;
      text-align: center;
      border-top: 1px solid #ccc;
      padding-top: 10px;
    }
    @media print {
      body { padding: 15px; }
      @page { size: A4; margin: 10mm; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <div class="header">
    ${logoUrl ? `<img src="${logoUrl}" alt="Jutem Fund" class="logo" />` : ""}
    <div class="statement-title">LOAN STATEMENT</div>
  </div>

  <div class="section-title">Client Details</div>
  <table>
    <tr><td style="width:40%; font-weight:bold;">Client Name</td><td>${values.client_name || ""}</td></tr>
    <tr><td style="font-weight:bold;">Client Identification Number</td><td>${values.client_id || ""}</td></tr>
    <tr><td style="font-weight:bold;">Contact Number</td><td>${values.contact_number || ""}</td></tr>
    <tr><td style="font-weight:bold;">Statement Date</td><td>${values.statement_date ? moment(values.statement_date).format("Do MMMM YYYY") : moment().format("Do MMMM YYYY")}</td></tr>
  </table>

  <div class="section-title">Loan Terms</div>
  <table>
    <thead><tr><th>Term</th><th>Value</th></tr></thead>
    <tbody>
      ${loanTermsRows}
    </tbody>
  </table>
  <table>
    <tr class="total-row"><td style="width:40%;">Total Expected Amount</td><td>${formatAmount(totalExpected)}</td></tr>
  </table>

  <div class="section-title">Payments Made</div>
  <table>
    <thead><tr><th>Date</th><th>Amount (ZMW)</th></tr></thead>
    <tbody>
      ${paymentRows || '<tr><td colspan="2" style="text-align:center;">No payments recorded</td></tr>'}
    </tbody>
  </table>
  <table>
    <tr class="total-row"><td style="width:40%;">Total Paid</td><td>${formatAmount(totalPaid)}</td></tr>
  </table>

  ${
    lateFee > 0
      ? `
  <div class="section-title">Late Fee (0.5% per day)</div>
  <table>
    <tr><td style="width:40%; font-weight:bold;">Late Fee Amount</td><td>${formatAmount(lateFee)}</td></tr>
    <tr><td style="font-weight:bold;">Days Past Due</td><td>${values.days_past_due || 0}</td></tr>
  </table>
  `
      : ""
  }

  ${
    interestExpense > 0
      ? `
  <div class="section-title">Interest Expense</div>
  <table>
    <tr><td style="width:40%; font-weight:bold;">Interest Expense Amount</td><td>${formatAmount(interestExpense)}</td></tr>
    <tr><td style="font-weight:bold;">Interest Due Date</td><td>${values.interest_due_date ? moment(values.interest_due_date).format("Do MMMM YYYY") : "N/A"}</td></tr>
  </table>
  `
      : ""
  }

  <div class="section-title">Balances</div>
  <table class="balance-table">
    <tr><td style="width:40%;">Balance Due</td><td>${formatAmount(balanceDue)}</td></tr>
    <tr><td>Balance Due After Payments</td><td>${formatAmount(balanceAfterPayments)}</td></tr>
  </table>

  <div class="footer">
    <p>This is a system-generated loan statement from Jutem Fund.</p>
    <p>Generated on ${moment().format("Do MMMM YYYY [at] HH:mm")}</p>
  </div>
</body>
</html>`;

      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, "_blank");

      if (printWindow) {
        printWindow.addEventListener("load", () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        });
      }

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 10000);

      message.success("Loan statement generated successfully!");
      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error("Error generating Jutem statement:", error);
      message.error("Failed to generate statement. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        generateStatementPDF(values);
      })
      .catch((err) => {
        console.log("Validation failed:", err);
      });
  };

  if (!loan || !borrower) return null;

  return (
    <>
      <Button
        icon={<FileTextOutlined />}
        onClick={openModal}
        loading={loading}
        {...buttonProps}
      >
        Generate Statement
      </Button>

      <Modal
        title="Jutem Fund Loan Statement Generator"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={700}
        bodyStyle={{ maxHeight: "75vh", overflowY: "auto" }}
      >
        <Form form={form} layout="vertical" onFinish={generateStatementPDF}>
          <Divider orientation="left" style={{ color: "#a8d4f0" }}>
            Client Information
          </Divider>
          <Form.Item
            label="Client Name"
            name="client_name"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Client ID Number"
            name="client_id"
          >
            <Input placeholder="e.g., 123456/67/8" />
          </Form.Item>
          <Form.Item label="Contact Number" name="contact_number">
            <Input placeholder="e.g., +260 971 234 567" />
          </Form.Item>
          <Form.Item
            label="Statement Date"
            name="statement_date"
            rules={[{ required: true }]}
          >
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>

          <Divider orientation="left" style={{ color: "#a8d4f0" }}>
            Loan Terms (Multiple Loans Supported)
          </Divider>
          <Form.List name="loans">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card
                    key={key}
                    size="small"
                    style={{ marginBottom: 12 }}
                    extra={
                      fields.length > 1 && (
                        <Button
                          type="link"
                          danger
                          onClick={() => remove(name)}
                          icon={<MinusCircleOutlined />}
                        >
                          Remove Loan
                        </Button>
                      )
                    }
                  >
                    <Form.Item
                      {...restField}
                      label="Principal Amount (ZMW)"
                      name={[name, "principal_amount"]}
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <Input type="number" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      label="Interest Rate (%)"
                      name={[name, "interest_rate"]}
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <Input type="number" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      label="Interest Period"
                      name={[name, "interest_period"]}
                    >
                      <Select>
                        <Option value="Per Period">Per Period</Option>
                        <Option value="Monthly">Monthly</Option>
                        <Option value="Quarterly">Quarterly</Option>
                        <Option value="Annually">Annually</Option>
                      </Select>
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      label="Expected Amount (ZMW)"
                      name={[name, "expected_amount"]}
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <Input type="number" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      label="Due Date"
                      name={[name, "due_date"]}
                    >
                      <DatePicker
                        style={{ width: "100%" }}
                        format="DD/MM/YYYY"
                      />
                    </Form.Item>
                  </Card>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                  style={{ marginBottom: 16 }}
                >
                  Add Loan
                </Button>
              </>
            )}
          </Form.List>

          <Divider orientation="left" style={{ color: "#a8d4f0" }}>
            Payments
          </Divider>
          <Form.List name="payments">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space
                    key={key}
                    style={{ display: "flex", marginBottom: 8 }}
                    align="baseline"
                  >
                    <Form.Item
                      {...restField}
                      name={[name, "amount"]}
                      rules={[{ required: true, message: "Amount required" }]}
                    >
                      <Input type="number" placeholder="Amount Paid (ZMW)" />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, "date"]}>
                      <DatePicker format="DD/MM/YYYY" />
                    </Form.Item>
                    <MinusCircleOutlined
                      onClick={() => remove(name)}
                      style={{ color: "red" }}
                    />
                  </Space>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                  style={{ marginBottom: 16 }}
                >
                  Add Payment
                </Button>
              </>
            )}
          </Form.List>

          <Divider orientation="left" style={{ color: "#a8d4f0" }}>
            Late Fee (0.5% per day)
          </Divider>
          <Form.Item label="Late Fee Amount (ZMW)" name="late_fee_amount">
            <Input type="number" placeholder="0 if none" />
          </Form.Item>
          <Form.Item label="Days Past Due" name="days_past_due">
            <Input type="number" placeholder="0 if none" />
          </Form.Item>

          <Divider orientation="left" style={{ color: "#a8d4f0" }}>
            Interest Expense
          </Divider>
          <Form.Item
            label="Interest Expense Amount (ZMW)"
            name="interest_expense_amount"
          >
            <Input type="number" placeholder="0 if none" />
          </Form.Item>
          <Form.Item label="Interest Due Date" name="interest_due_date">
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>

          <Button
            type="primary"
            htmlType="submit"
            block
            size="large"
            loading={loading}
            style={{
              marginTop: 16,
              backgroundColor: "#2e7d32",
              borderColor: "#2e7d32",
              height: 48,
              fontSize: 16,
            }}
          >
            Generate Statement
          </Button>
        </Form>
      </Modal>
    </>
  );
};

export default JutemStatementGenerator;
